import type { WrappedFieldsProps } from "redux-form";
import { Fields } from "redux-form";

import { Box, Typography } from "@braintrust/braintrust-ui-components";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { FieldSectionPanel } from "@js/apps/jobs/forms/fields";
import { CheckboxField } from "@js/forms/fields/checkbox";
import { getError, isError, isTouched } from "@js/forms/utils";

import { AmountField } from "./amount-field";
import { RATE_FIELDS_KEYS } from "./constants";
import { getFieldsToCheckMeta } from "./helpers";
import { PaymentTypeField } from "./payment-type-field";
import { rateValidator } from "./validators";

export const RateSectionModule = () => {
  return (
    <Fields
      id="payment_type"
      names={[
        RATE_FIELDS_KEYS.hourly_rate,
        RATE_FIELDS_KEYS.payment_type,
        RATE_FIELDS_KEYS.fixed_rate,
        RATE_FIELDS_KEYS.min_rate,
        RATE_FIELDS_KEYS.max_rate,
        RATE_FIELDS_KEYS.min_annual_rate,
        RATE_FIELDS_KEYS.max_annual_rate,
        RATE_FIELDS_KEYS.job_type,
        RATE_FIELDS_KEYS.custom_min_rate,
        RATE_FIELDS_KEYS.custom_max_rate,
        RATE_FIELDS_KEYS.min_monthly_rate,
        RATE_FIELDS_KEYS.max_monthly_rate,
        RATE_FIELDS_KEYS.talent_invoice_editing_disabled,
      ]}
      validate={[rateValidator]}
      component={Rate}
    />
  );
};

const Rate = ({
  job_type,
  payment_type,
  fixed_rate,
  min_rate,
  max_rate,
  min_annual_rate,
  max_annual_rate,
  hourly_rate,
  custom_min_rate,
  custom_max_rate,
  min_monthly_rate,
  max_monthly_rate,
  talent_invoice_editing_disabled,
}: WrappedFieldsProps) => {
  const selectedPaymentType: EnumType<typeof ENUMS.JobPaymentType> =
    payment_type.input.value;
  const fields = getFieldsToCheckMeta(selectedPaymentType, [
    fixed_rate,
    min_rate,
    max_rate,
    custom_min_rate,
    custom_max_rate,
    min_annual_rate,
    max_annual_rate,
    hourly_rate,
    payment_type,
    min_monthly_rate,
    max_monthly_rate,
    talent_invoice_editing_disabled,
  ]);

  const isErrorInSection = isError(fields);
  const isAnyTouched = isTouched(fields);
  const displayError = isErrorInSection && isAnyTouched;
  const isNodeOrImpersonated = useIsNodeOrImpersonated();

  return (
    <FieldSectionPanel
      title="Rate"
      id="rate"
      description="How Talent will be paid"
      isError={displayError}
    >
      {displayError && (
        <Typography component="p" variant="paragraph" error sx={{ mb: 2 }}>
          {getError(fields)}
        </Typography>
      )}

      <Box sx={{ mb: 3 }}>
        <PaymentTypeField
          job_type={job_type}
          payment_type={payment_type}
          talent_invoice_editing_disabled={talent_invoice_editing_disabled}
        />
      </Box>

      <AmountField
        payment_type={payment_type}
        fixed_rate={fixed_rate}
        min_rate={min_rate}
        max_rate={max_rate}
        min_annual_rate={min_annual_rate}
        max_annual_rate={max_annual_rate}
        hourly_rate={hourly_rate}
        custom_min_rate={custom_min_rate}
        custom_max_rate={custom_max_rate}
        min_monthly_rate={min_monthly_rate}
        max_monthly_rate={max_monthly_rate}
      />
      {isNodeOrImpersonated &&
        selectedPaymentType === ENUMS.JobPaymentType.FIXED_PROJECT && (
          <Box sx={{ mt: 2 }}>
            <CheckboxField
              hideError
              label="Disable Talent Invoice Editing"
              meta={talent_invoice_editing_disabled.meta}
              input={talent_invoice_editing_disabled.input}
            />
          </Box>
        )}
    </FieldSectionPanel>
  );
};
